import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { BRAND } from '../lib/branding';
import { MessageCircle, X, Send, Phone } from 'lucide-react';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState({ phone: BRAND.phoneDigits, phone_display: BRAND.phone, whatsapp_url: BRAND.whatsappUrl });
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Cargar config al inicio
  useEffect(() => {
    fetch(`${API_URL}/api/chatbot/config`)
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(() => {});
  }, [API_URL]);

  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mostrar aviso de IA + saludo al abrir
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: `a-ai-disclosure-${Date.now()}`,
        role: 'assistant',
        content: 'Estás hablando con un asistente virtual basado en Inteligencia Artificial.',
        isDisclosure: true
      }]);
      sendMessage('hola', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sendMessage = async (text, isAutomatic = false) => {
    if (!text.trim()) return;

    // Añadir mensaje del usuario (solo si no es automático)
    if (!isAutomatic) {
      setMessages(prev => [...prev, { id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role: 'user', content: text }]);
    }
    
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text })
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        content: data.message,
        buttons: data.buttons || []
      }]);
    } catch (error) {
      // Respuesta fallback si falla la API
      setMessages(prev => [...prev, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        content: '¡Hola! Soy el asistente de CG Valet Parking. ¿En qué puedo ayudarte?',
        buttons: [
          { text: '🚗 Reservar ahora', action: 'navigate', url: '/reservar' },
          { text: '📞 Contactar', action: 'whatsapp', url: config.whatsapp_url }
        ]
      }]);
    }

    setIsLoading(false);
  };

  const handleButtonClick = (button) => {
    if (button.action === 'navigate') {
      window.location.href = button.url;
    } else if (button.action === 'whatsapp' || button.action === 'call') {
      window.open(button.url, '_blank');
    } else if (button.action === 'message') {
      sendMessage(button.value || button.text);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  // Formatear mensaje con markdown básico y sanitizar HTML para prevenir XSS
  const formatMessage = (text) => {
    const rawHtml = String(text || '')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
    return DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['strong', 'em', 'br', 'b', 'i'],
      ALLOWED_ATTR: []
    });
  };

  return (
    <>
      {/* Botón flotante */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-6 right-6 z-50 bg-amber-500 hover:bg-amber-600 text-black rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Ventana del chat */}
      {isOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          style={{ height: '500px', maxHeight: 'calc(100vh - 100px)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                <span className="text-lg">🚗</span>
              </div>
              <div>
                <h3 className="font-semibold text-sm">CG Valet Parking</h3>
                <p className="text-xs text-gray-300">Asistente virtual</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close-btn"
              className="text-gray-300 hover:text-white p-1"
              aria-label="Cerrar chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={msg.id || `msg-${idx}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-amber-500 text-black rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  <div
                    className="text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                  />
                  
                  {/* Botones */}
                  {msg.buttons && msg.buttons.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2">
                      {msg.buttons.map((btn, btnIdx) => (
                        <button
                          key={`${msg.id || idx}-btn-${btn.action || ''}-${btn.text}`}
                          onClick={() => handleButtonClick(btn)}
                          className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 text-amber-400 font-medium text-sm rounded-lg transition-colors border border-gray-700"
                        >
                          {btn.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                data-testid="chatbot-input"
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-900 bg-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                disabled={isLoading}
              />
              <button
                type="submit"
                data-testid="chatbot-send-btn"
                disabled={isLoading || !input.trim()}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-black p-2 rounded-full transition-colors"
                aria-label="Enviar mensaje"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            {/* Quick contact */}
            <div className="mt-2 flex justify-center">
              <a
                href={config.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-amber-600 flex items-center gap-1"
              >
                <Phone className="w-3 h-3" />
                ¿Prefieres hablar? {config.phone_display || BRAND.phone}
              </a>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
